import { CELL_PX, Sprites } from "./sprites";
/** Canvas pixels per Unity unit for a board whose cells are `cs` px wide (a cell is 1.485 units). */
function unit(cs) {
    return (cs / CELL_PX) * 100;
}
// Draws `img` centred on (cx, cy): natural size x `scale` (or `w` x `h` sprite px when given), turned by
// `angle` radians clockwise, mirrored by flipX / flipY — in that order, as Unity applies them.
function put(ctx, img, cx, cy, k, opts = {}) {
    if (!img)
        return;
    const dw = (opts.w ?? img.naturalWidth) * k;
    const dh = (opts.h ?? img.naturalHeight) * k;
    ctx.save();
    ctx.translate(cx, cy);
    if (opts.angle)
        ctx.rotate(opts.angle);
    ctx.scale(opts.flipX ? -1 : 1, opts.flipY ? -1 : 1);
    ctx.drawImage(img, -dw / 2, -dh / 2, dw, dh);
    ctx.restore();
}
// A count in the game's label style: bold, light face, dark outline.
function label(ctx, text, cx, cy, px, fill, outline) {
    ctx.save();
    ctx.font = `900 ${Math.round(px)}px "Segoe UI", Arial, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.lineJoin = "round";
    ctx.lineWidth = Math.max(2, px * 0.22);
    ctx.strokeStyle = outline;
    ctx.strokeText(text, cx, cy);
    ctx.fillStyle = fill;
    ctx.fillText(text, cx, cy);
    ctx.restore();
}
const cellCentre = (p, cs) => ({ x: (p.x + 0.5) * cs, y: (p.y + 0.5) * cs });
// --- cell items (cell-local: the cell is [0,S]x[0,S]) ------------------------------------------------------
/**
 * Super tunnel (MarbleGrid.BuildSuperTunnel): at tunnel scale 0.98 and offset (0, -0.025), resting with its
 * mouth DOWN (it only turns when it serves a neighbour). Shadow, pipe, mouth patch, then the load count.
 */
export function drawSuperTunnelSprite(ctx, S, count) {
    const pipe = Sprites.single("itemSuperTunnelTunnel");
    if (!pipe)
        return false;
    const U = unit(S);
    const k = (S / CELL_PX) * 0.98;
    const c = S / 2;
    put(ctx, Sprites.single("itemSuperTunnelTunnelShadow"), c, c + 0.1367 * U, k);
    put(ctx, pipe, c, c + 0.025 * U, k);
    put(ctx, Sprites.single("itemSuperTunnelTunnelPatch"), c, c + 0.0456 * U, k);
    if (count > 0)
        label(ctx, String(count), c, c + 0.076 * U, 0.5 * U, "#FFF6E6", "#484290");
    return true;
}
/** Tool box (MarbleGrid.BuildToolBox): the crate on the cell centre, its load written on its face. */
export function drawToolBoxSprite(ctx, S, count) {
    const crate = Sprites.single("itemToolBoxToolBox");
    if (!crate)
        return false;
    const U = unit(S);
    const c = S / 2;
    put(ctx, crate, c, c, S / CELL_PX);
    if (count > 0)
        label(ctx, String(count), c, c - 0.163 * U, 0.55 * U, "#FFF8DE", "#3B2A6B");
    return true;
}
// --- the pipe (board-level: it reaches up the column above its own cell) ------------------------------------
// Draws `img` with its PIVOT (a share of its size from the bottom-left, Unity's way) on (px, py), `k` canvas px
// per sprite px, optionally stretched to `h` sprite px tall.
function putPivot(ctx, img, px, py, k, pivX, pivY, h) {
    if (!img)
        return;
    const W = img.naturalWidth, H = h ?? img.naturalHeight;
    put(ctx, img, px + (0.5 - pivX) * W * k, py - (0.5 - pivY) * H * k, k, { h: H });
}
// The game's numbers (Assets/Prefabs/Pipe.prefab + Pipe.cs, art-gen/out/pipe/pipe_kit.json), in Unity units,
// y UP. The item sits like a tunnel (cell centre + TunnelOffset (0, -0.025), at TunnelScale 0.98) plus the
// pipe's own _positionOffset (0, -0.0906); "local" is the pipe's own space, art px / 100 at item scale 1.
export const PIPE = {
    tunnelOffsetY: -0.025,
    positionOffsetY: -0.0906,
    scale: 0.98,
    bodyFrom: 0.5854, // _bodyFrom (local)
    bodyShadowFrom: 0.8154, // _bodyShadowFrom (local)
    shadowOffsetY: -0.1, // _shadowOffset (local)
    bodyShadowTop: 0.12, // _bodyShadowTopOffset (local)
    wallLineOffset: -0.34, // _wallLineOffset (WORLD)
    countY: 0.153, // BoardFront/CountText (local)
    stripPx: 32, // the strips' own height
    pivots: { head: 0.442468, headShadow: 0.52622, wallEnd: 0.50495 },
};
/**
 * Pipe (MarbleGrid.BuildPipe + Pipe.LayoutBody): the head on its cell, its body running straight up to the top
 * of `topRow` - the topmost cell of its column the template leaves empty, as the game claims it - where the
 * collar sits, on the wall line the game puts it (the editor has no wall, so the top edge of that cell stands
 * in for the wall's underside). Shadows, body, collar, head, then the load count over the head.
 */
export function drawPipeSprite(ctx, cs, head, topRow, count) {
    const headImg = Sprites.single("itemPipeHead");
    const body = Sprites.single("itemPipeBody");
    const wallEnd = Sprites.single("itemPipeWallEnd");
    if (!headImg || !body || !wallEnd)
        return false;
    const U = unit(cs); // canvas px per Unity unit
    const L = U * PIPE.scale; // canvas px per pipe-local unit
    const k = (cs / CELL_PX) * PIPE.scale; // canvas px per sprite px
    const x = (head.x + 0.5) * cs;
    const oy = (head.y + 0.5) * cs - (PIPE.tunnelOffsetY + PIPE.positionOffsetY) * U; // the item's origin
    const endY = topRow * cs - PIPE.wallLineOffset * U; // the cut, inside the collar
    const local = (canvasY) => (oy - canvasY) / L; // canvas y -> local up
    const end = local(endY);
    const strip = (img, from, to) => {
        if (!img || to - from <= 1e-3)
            return;
        putPivot(ctx, img, x, oy - from * L, k, 0.5, 0, ((to - from) * 100));
    };
    putPivot(ctx, Sprites.single("itemPipeHeadShadow"), x, oy, k, 0.5, PIPE.pivots.headShadow);
    strip(Sprites.single("itemPipeBodyShadow"), PIPE.bodyShadowFrom + PIPE.shadowOffsetY, end + PIPE.bodyShadowTop + PIPE.shadowOffsetY);
    strip(body, PIPE.bodyFrom, end);
    putPivot(ctx, wallEnd, x, oy - end * L, k, 0.5, PIPE.pivots.wallEnd);
    putPivot(ctx, headImg, x, oy, k, 0.5, PIPE.pivots.head);
    if (count > 0)
        label(ctx, String(count), x, oy - PIPE.countY * L, 0.5 * U, "#FFF6E6", "#484290");
    return true;
}
// --- single-box covers (absolute: the covered cell's top-left at x, y, size S) ------------------------------
/** Ice (FrozenBoxModifier): fresh ice at (0, -0.037); one hit left shows the cracked art, nudged (0.01, 0.005). */
export function drawIceSprite(ctx, x, y, S, hits) {
    const cracked = hits === 1;
    const img = Sprites.single(cracked ? "itemFrozenBoxFrozenBoxBroken" : "itemFrozenBoxFrozenBox");
    if (!img)
        return false;
    const U = unit(S);
    const ox = cracked ? 0.01 : 0;
    const oy = cracked ? -0.032 : -0.037;
    put(ctx, img, x + S / 2 + ox * U, y + S / 2 - oy * U, S / CELL_PX);
    return true;
}
/** Super mystery box (SuperMysteryBoxModifier, closed): its body over the hidden box, the lid raised 0.101. */
export function drawSuperMysterySprite(ctx, x, y, S) {
    const body = Sprites.single("itemSuperMysteryBoxHiddenScrewBoxBody");
    if (!body)
        return false;
    const U = unit(S);
    const k = S / CELL_PX;
    const cx = x + S / 2;
    const cy = y + S / 2;
    put(ctx, body, cx, cy, k);
    put(ctx, Sprites.single("itemSuperMysteryBoxHiddenScrewBoxCover01Shadow"), cx, cy - 0.101 * U, k);
    put(ctx, Sprites.single("itemSuperMysteryBoxHiddenScrewBoxCover01"), cx, cy - 0.101 * U, k);
    return true;
}
// --- area and run items (absolute canvas coords) -------------------------------------------------------------
/**
 * Metal box (MetalBoxModifier) over the rect x, y, w, h: the body on the area's centre, chain 2 then chain 1
 * over it (each with its shadow), 0.177 up. Three hits left shows both chains, two shows chain 2, one none.
 * An area other than 2x2 stretches the whole thing, as the game does.
 */
export function drawMetalBoxSprite(ctx, x, y, w, h, cs, hits) {
    const body = Sprites.single("itemMetalBoxMetalBoxBody");
    if (!body)
        return false;
    const U = unit(cs);
    const k = cs / CELL_PX;
    const left = hits > 0 ? hits : 3;
    ctx.save();
    ctx.translate(x + w / 2, y + h / 2);
    ctx.scale(w / (2 * cs), h / (2 * cs));
    put(ctx, body, 0, 0, k);
    if (left >= 2) {
        put(ctx, Sprites.single("itemMetalBoxMetalBoxChain02Shadow"), 0, -0.147 * U, k);
        put(ctx, Sprites.single("itemMetalBoxMetalBoxChain02"), 0, -0.177 * U, k);
    }
    if (left >= 3) {
        put(ctx, Sprites.single("itemMetalBoxMetalBoxChain01Shadow"), 0, -0.147 * U, k);
        put(ctx, Sprites.single("itemMetalBoxMetalBoxChain01"), 0, -0.177 * U, k);
    }
    ctx.restore();
    return true;
}
export function drawElevatorSprite(ctx, x, y, w, h, cs) {
    const body = Sprites.single("itemElevatorBody");
    if (!body)
        return false;
    const U = unit(cs);
    const k = cs / CELL_PX;
    const cx = x + w / 2;
    const cy = y + h / 2 + 0.1 * U;
    put(ctx, Sprites.single("itemElevatorDoorL"), cx - 0.725 * U, cy, k);
    put(ctx, Sprites.single("itemElevatorDoorR"), cx + 0.725 * U, cy, k);
    put(ctx, body, cx, cy, k);
    return true;
}
export function drawElevatorPatchSprite(ctx, x, y, w, h, cs) {
    const U = unit(cs);
    put(ctx, Sprites.single("itemElevatorBodyPatch"), x + w / 2, y + h / 2 + 0.1 * U + 1.238 * U, cs / CELL_PX);
}
/**
 * Bolt (BoltModifier) from its HEAD to `far`: the plate, the bolt and the plate's patch on the run's centre,
 * never rotated — a vertical run uses the vertical art. The art has its head on the left / top, so the bolt
 * (only the bolt) flips when the head is on the right / bottom. The art is made for 3 cells and is never
 * stretched on a flat board, so other lengths draw it at that size.
 */
export function drawBoltSprite(ctx, head, far, cs) {
    const vertical = head.x === far.x;
    const bolt = Sprites.single(vertical ? "itemBoltEyeBoltBoltVertical" : "itemBoltEyeBoltBoltHorizontal");
    if (!bolt)
        return false;
    const U = unit(cs);
    const k = cs / CELL_PX;
    const a = cellCentre(head, cs);
    const b = cellCentre(far, cs);
    const cx = (a.x + b.x) / 2;
    const cy = (a.y + b.y) / 2;
    put(ctx, Sprites.single(vertical ? "itemBoltEyeBoltMiddleVertical" : "itemBoltEyeBoltMiddleHorizontal"), cx, cy, k);
    put(ctx, bolt, cx, cy, k, { flipX: !vertical && head.x > far.x, flipY: vertical && head.y > far.y });
    put(ctx, Sprites.single(vertical ? "itemBoltEyeBoltPatchVertical" : "itemBoltEyeBoltPatchHorizontal"), cx, cy - 0.042 * U, k);
    return true;
}
/**
 * Sliding door (SlidingDoorModifier) over `cells` (the run walked from the ANCHOR) at its current `length`:
 * the housing on the anchor, the rail pieces and the tip laid out rigidly from it — the steps are the game's
 * (the prefab's body step, then the rail sprite's own length). Every piece flips when the anchor is on the
 * right / at the bottom. Draw order: rail, tip, housing on top.
 */
export function drawSlidingDoorSprite(ctx, cells, length, cs) {
    if (cells.length < 2)
        return false;
    const vertical = cells[0].x === cells[1].x;
    const body = Sprites.single(vertical ? "itemSlidingDoorBodyVertical" : "itemSlidingDoorBodyHorizontal");
    const middle = Sprites.single(vertical ? "itemSlidingDoorMiddleVertical" : "itemSlidingDoorMiddleHorizontal");
    const tip = Sprites.single(vertical ? "itemSlidingDoorTipVertical" : "itemSlidingDoorTipHorizontal");
    if (!body || !middle || !tip)
        return false;
    const U = unit(cs);
    const k = cs / CELL_PX;
    const L = Math.max(2, Math.min(cells.length, length > 0 ? length : cells.length));
    const anchor = cellCentre(cells[0], cs);
    // Screen direction the door runs in, and whether the art is mirrored (anchor on the right / bottom).
    const dir = vertical ? Math.sign(cells[1].y - cells[0].y) : Math.sign(cells[1].x - cells[0].x);
    const flip = dir < 0;
    const bodyStep = vertical ? 1.535 : 1.46;
    const railStep = vertical ? 1.52 : 1.47;
    const at = (d) => vertical ? { x: anchor.x, y: anchor.y + dir * d * U } : { x: anchor.x + dir * d * U, y: anchor.y };
    const opts = { flipX: !vertical && flip, flipY: vertical && flip };
    for (let i = 1; i <= L - 2; i++) {
        const p = at(bodyStep + railStep * (i - 1));
        put(ctx, middle, p.x, p.y, k, opts);
    }
    // The tip sits one tip step past the last rail piece (the anchor's body step when there is none).
    const tipDist = L === 2 ? (vertical ? 1.52 : 1.51) : (vertical ? 3.04 : 2.98) + railStep * (L - 3);
    const tp = at(tipDist);
    put(ctx, tip, tp.x, tp.y, k, opts);
    put(ctx, body, anchor.x, anchor.y, k, opts);
    return true;
}
// The laser pair's sprites: Purple is pair 0, Blue pair 1 (LaserGatePair in the prefab). The beam is shared.
function laserSet(pair) {
    const blue = pair === 1;
    return {
        gate: Sprites.single(blue ? "itemLaserGateBlueLaserGate" : "itemLaserGateLaserGate"),
        lock: Sprites.single(blue ? "itemLaserGateBlueLaserLock" : "itemLaserGateLaserLock"),
        key: Sprites.single(blue ? "itemLaserGateBlueKeyFrame001" : "itemLaserGateKeyFrame001"),
        beam: Sprites.single("itemLaserGateLaser"),
    };
}
// One tiled beam (Laser.png, 6 px caps, tiled middle) `len` units long and 0.231 thick, centred on (cx, cy)
// and turned by `angle`.
function drawBeam(ctx, img, cx, cy, len, angle, U) {
    const iw = img.naturalWidth;
    const ih = img.naturalHeight;
    const cap = 6;
    const lw = len * U;
    const th = 0.231 * U;
    const capW = (cap / 100) * U;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(angle);
    ctx.drawImage(img, 0, 0, cap, ih, -lw / 2, -th / 2, capW, th);
    ctx.drawImage(img, cap, 0, iw - 2 * cap, ih, -lw / 2 + capW, -th / 2, Math.max(0, lw - 2 * capW), th);
    ctx.drawImage(img, iw - cap, 0, cap, ih, lw / 2 - capW, -th / 2, capW, th);
    ctx.restore();
}
/**
 * Laser gate (LaserGateModifier.BuildGate) between END A and END B, in the authored order — the game does not
 * sort them. Three beams per side, a gate post on each end (A mirrored), the lock on the midpoint. The
 * sideways nudges are the game's and are tuned for horizontal runs; a vertical gate draws as the game draws it.
 */
export function drawLaserGateSprite(ctx, a, b, cs, pair) {
    const set = laserSet(pair);
    if (!set.gate || !set.lock || !set.beam)
        return false;
    const U = unit(cs);
    const k = cs / CELL_PX;
    const ca = cellCentre(a, cs);
    const cb = cellCentre(b, cs);
    const mid = { x: (ca.x + cb.x) / 2, y: (ca.y + cb.y) / 2 };
    // Unity units, y up: half is EndB - midpoint; axis its direction; across its left normal.
    const half = { x: (cb.x - ca.x) / 2 / U, y: -(cb.y - ca.y) / 2 / U };
    const len = Math.hypot(half.x, half.y) || 1;
    const axis = { x: half.x / len, y: half.y / len };
    const across = { x: -axis.y, y: axis.x };
    const angle = -Math.atan2(axis.y, axis.x); // canvas turn for Unity's z angle
    const px = (ux, uy) => ({ x: mid.x + ux * U, y: mid.y - uy * U });
    for (const nub of [0.43, 0.13, -0.18]) {
        const pa = px(-half.x / 2 + across.x * nub - 0.15, -half.y / 2 + across.y * nub);
        const pb = px(half.x / 2 + across.x * nub + 0.15, half.y / 2 + across.y * nub);
        drawBeam(ctx, set.beam, pa.x, pa.y, len, angle, U);
        drawBeam(ctx, set.beam, pb.x, pb.y, len, angle, U);
    }
    const ga = px(-half.x - 0.325, -half.y + 0.05);
    const gb = px(half.x + 0.325, half.y + 0.05);
    put(ctx, set.gate, ga.x, ga.y, k, { angle, flipX: true });
    put(ctx, set.gate, gb.x, gb.y, k, { angle });
    const lk = px(0, 0.075);
    put(ctx, set.lock, lk.x, lk.y, k);
    return true;
}
/** The laser's key (LaserGateKey, first frame) floating on its key box's centre. */
export function drawLaserKeySprite(ctx, key, cs, pair) {
    const img = laserSet(pair).key;
    if (!img)
        return false;
    const c = cellCentre(key, cs);
    put(ctx, img, c.x, c.y, cs / CELL_PX);
    return true;
}
